/** Edit a grid preset: name, shared flag, and the raw settings JSON
 *  (jsoneditor in code mode). Management-page only (console_presets_manage
 *  route gate); the backend still enforces ownership rules. */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import * as presetsApi from '@/api/presets';
import {
  presetErrorMessage,
  presetSettingsSchema,
  type PresetSettingsV1,
  type PresetVisibility,
} from '@/api/presets';
import { JsonEditorWidget, type JsonEditorHandle } from '@/components/JsonEditor/JsonEditorWidget';
import { useSnackbar } from '@/components/Snackbar';

interface Props {
  presetId: number;
  onClose: () => void;
}

export function PresetEditDialog({ presetId, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const editorRef = useRef<JsonEditorHandle>(null);
  const [name, setName] = useState<string | null>(null); // null until seeded
  const [visibility, setVisibility] = useState<PresetVisibility | null>(null);
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['gridPreset', presetId],
    queryFn: () => presetsApi.retrieve(presetId),
  });
  const seededName = name ?? detail.data?.name ?? '';
  const seededVisibility = visibility ?? detail.data?.visibility ?? 'private';

  const mutation = useMutation({
    mutationFn: (patch: {
      name: string;
      visibility: PresetVisibility;
      settings: PresetSettingsV1;
    }) => presetsApi.update(presetId, patch),
    onSuccess: (p) => {
      qc.setQueryData(['gridPreset', p.id], p);
      void qc.invalidateQueries({ queryKey: ['gridPresetsAll'] });
      void qc.invalidateQueries({ queryKey: ['gridPresets', p.grid] });
      void qc.invalidateQueries({ queryKey: ['gridPresetHistory', p.id] });
      snackbar.show('Preset updated.', 'success');
      onClose();
    },
    onError: (e: unknown) => setError(presetErrorMessage(e)),
  });

  const submit = () => {
    setError(null);
    let raw: Record<string, unknown>;
    try {
      raw = editorRef.current?.getValue() ?? {};
    } catch {
      setError('Settings is not valid JSON.');
      return;
    }
    const parsed = presetSettingsSchema.safeParse(raw);
    if (!parsed.success) {
      setError(`Settings blob rejected: ${parsed.error.issues[0]?.message ?? 'invalid shape'}`);
      return;
    }
    mutation.mutate({
      name: seededName.trim(),
      visibility: seededVisibility,
      settings: parsed.data as PresetSettingsV1,
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit preset #{presetId}
        {detail.data ? ` — ${detail.data.grid}` : ''}
      </DialogTitle>
      <DialogContent>
        {detail.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {detail.isError && <Alert severity="error">Couldn’t load the preset.</Alert>}
        {detail.data && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Name"
                size="small"
                fullWidth
                value={seededName}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                select
                size="small"
                label="Visibility"
                value={seededVisibility}
                onChange={(e) => setVisibility(e.target.value as PresetVisibility)}
                sx={{ width: 160, flexShrink: 0 }}
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="shared">Shared</MenuItem>
              </TextField>
            </Stack>
            <Box sx={{ height: 380 }}>
              <JsonEditorWidget
                ref={editorRef}
                initialValue={detail.data.settings as unknown as Record<string, unknown>}
                mode="code"
              />
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!detail.data || !seededName.trim() || mutation.isPending}
          onClick={submit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
